import React, { useState } from 'react';
import { 
    BookOpen, Target, TrendingUp, Shield, BarChart3, 
    ArrowRight, Activity, Scale, Calculator, AlertTriangle, 
    PieChart, GraduationCap, Coins, Search, Layers, CheckCircle, XCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GuidePage = () => {
    const { t, i18n } = useTranslation();
    const [activeTab, setActiveTab] = useState('CQP');
    const isEn = i18n.language && i18n.language.startsWith('en');

    const categories = [
        { id: 'CQP', label: t('guide.tab_cqp'), icon: Target, color: 'rose' },
        { id: 'QUANTAMENTAL', label: t('guide.tab_algo'), icon: Layers, color: 'indigo' },
        { id: 'RADAR', label: isEn ? 'Tactical Radar' : 'Radar Tactique', icon: Activity, color: 'emerald' },
        { id: 'VALUATION', label: t('guide.tab_val'), icon: Scale, color: 'indigo' },
        { id: 'QUALITY', label: t('guide.tab_qual'), icon: TrendingUp, color: 'emerald' },
        { id: 'HEALTH', label: t('guide.tab_health'), icon: Shield, color: 'blue' },
        { id: 'CONCEPTS', label: t('guide.tab_concepts'), icon: BookOpen, color: 'purple' },
        { id: 'ACCOUNTING', label: t('guide.tab_accounting'), icon: PieChart, color: 'slate' },
    ];

    return (
        <div className="min-h-screen bg-slate-50 pt-24 pb-20 font-sans text-slate-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-bold mb-6">
                        <GraduationCap size={18} /> {isEn ? "KeyForIn Academy" : "Académie KeyForIn"}
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
                        {t('guide.title')} <span className="text-indigo-600"><br className="hidden md:block"/>{t('guide.subtitle')}</span>
                    </h1>
                    <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
                        {t('guide.desc')}
                    </p>
                </div>

                <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {categories.map((cat) => {
                        const Icon = cat.icon;
                        const isActive = activeTab === cat.id;
                        return (
                            <button
                                key={cat.id}
                                onClick={() => setActiveTab(cat.id)}
                                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold transition-all duration-200 border-2 ${
                                    isActive 
                                    ? `bg-${cat.color}-600 border-${cat.color}-600 text-white shadow-lg scale-105` 
                                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                            >
                                <Icon size={18} className={isActive ? 'text-white' : `text-${cat.color}-500`} /> 
                                {cat.label}
                            </button>
                        );
                    })}
                </div>

                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    {activeTab === 'ACCOUNTING' ? (
                        <BalanceSheetVisual isEn={isEn} />
                    ) : activeTab === 'CQP' ? (
                        <CQPExplainer isEn={isEn} />
                    ) : activeTab === 'QUANTAMENTAL' ? (
                        <QuantamentalExplainer isEn={isEn} />
                    ) : activeTab === 'RADAR' ? (
                        <RadarExplainer isEn={isEn} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {(isEn ? definitionsEn : definitionsFr)[activeTab]?.map((item, index) => (
                                <MetricCard key={index} item={item} isEn={isEn} />
                            ))}
                        </div>
                    )}
                </div>

                <div className="mt-20 bg-slate-900 rounded-3xl p-12 text-center text-white shadow-xl relative overflow-hidden">
                    <div className="relative z-10">
                        <h3 className="text-3xl font-black mb-4">{t('guide.cta_title')}</h3>
                        <p className="text-slate-400 mb-8 text-lg max-w-xl mx-auto">
                            {t('guide.cta_desc')}
                        </p>
                        <a href="/screener" className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-500 text-white font-black rounded-xl hover:bg-indigo-400 transition-all transform hover:scale-105 shadow-lg">
                            <Search size={20}/> {t('guide.cta_btn')}
                        </a>
                    </div>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600 rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-600 rounded-full blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>
                </div>

            </div>
        </div>
    );
};

const RadarExplainer = ({ isEn }) => (
    <div className="max-w-5xl mx-auto space-y-8">
        <div className="bg-slate-900 rounded-3xl border border-slate-800 p-8 shadow-lg text-center text-white">
            <h2 className="text-3xl font-black mb-4">{isEn ? "Tactical Radar:" : "Le Radar Tactique :"} <span className="text-emerald-400">{isEn ? "Timing & Trend" : "Timing & Tendance"}</span></h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
                {isEn 
                ? "The Tactical Radar gives you a short and medium-term view based on price action and moving averages. It helps you identify the best moments to buy (oversold) or sell (overbought) your favorite assets." 
                : "Le Radar Tactique vous offre une vue à court et moyen terme basée sur l'action des prix et les moyennes mobiles. Il vous aide à identifier les meilleurs moments pour acheter (survente) ou alléger (surachat) vos actifs préférés."}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* L'Élastique RSI */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-purple-100 text-purple-600 rounded-xl"><Activity size={24}/></div>
                    <h3 className="text-xl font-black text-slate-900">{isEn ? "RSI (14) - The Market's Rubber Band" : "RSI (14) - L'Élastique du Marché"}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6">
                    {isEn 
                    ? "The Relative Strength Index measures the speed and magnitude of recent price changes. It oscillates between 0 and 100." 
                    : "Le Relative Strength Index (RSI) mesure la vitesse et l'ampleur des variations récentes des prix. Il oscille de 0 à 100."}
                </p>
                <div className="space-y-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-700 text-sm">{isEn ? "Below 30" : "Sous 30"}</span>
                        <span className="px-3 py-1 rounded text-xs font-black bg-emerald-100 text-emerald-700">21</span>
                        <span className="text-sm font-medium text-slate-600 text-right w-1/2">{isEn ? "Oversold. The stock crashed too fast, potential rebound." : "Survente. L'action a chuté trop vite, rebond potentiel."}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                        <span className="font-bold text-slate-700 text-sm">{isEn ? "Between 40-60" : "Entre 40-60"}</span>
                        <span className="px-3 py-1 rounded text-xs font-black bg-slate-200 text-slate-700">50</span>
                        <span className="text-sm font-medium text-slate-600 text-right w-1/2">{isEn ? "Neutral. Normal price action." : "Neutre. Mouvements de prix normaux."}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                        <span className="font-bold text-slate-700 text-sm">{isEn ? "Above 70" : "Au-dessus de 70"}</span>
                        <span className="px-3 py-1 rounded text-xs font-black bg-rose-100 text-rose-700">83</span>
                        <span className="text-sm font-medium text-slate-600 text-right w-1/2">{isEn ? "Overbought. The stock surged too fast, potential correction." : "Surachat. L'action est montée trop vite, correction possible."}</span>
                    </div>
                </div>
            </div>

            {/* Moyennes Mobiles */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-blue-100 text-blue-600 rounded-xl"><TrendingUp size={24}/></div>
                    <h3 className="text-xl font-black text-slate-900">{isEn ? "SMA 50 & 200 - The Trends" : "Tendance & Fond (SMA)"}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-6">
                    {isEn 
                    ? "The percentages show how far the current price is from its Simple Moving Averages (50 days for medium trend, 200 days for long-term background)." 
                    : "Les pourcentages indiquent la distance entre le prix actuel et ses Moyennes Mobiles Simples (50 jours pour le moyen terme, 200 jours pour la tendance de fond)."}
                </p>
                <div className="space-y-4">
                    <div className="bg-rose-50 border border-rose-100 p-4 rounded-xl flex items-center justify-between">
                        <div>
                            <div className="text-xs font-bold text-rose-800 uppercase mb-1">Tendance (SMA 50)</div>
                            <div className="font-mono text-slate-500 text-sm">232.92</div>
                        </div>
                        <div className="text-right">
                            <span className="inline-block px-2 py-1 bg-white text-rose-600 font-black text-sm rounded shadow-sm">-11.98%</span>
                            <div className="text-[10px] text-rose-700 mt-1">{isEn ? "Price is below the average" : "Le prix est SOUS la moyenne"}</div>
                        </div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-center justify-between">
                        <div>
                            <div className="text-xs font-bold text-emerald-800 uppercase mb-1">Fond (SMA 200)</div>
                            <div className="font-mono text-slate-500 text-sm">227.38</div>
                        </div>
                        <div className="text-right">
                            <span className="inline-block px-2 py-1 bg-white text-emerald-600 font-black text-sm rounded shadow-sm">+17.98%</span>
                            <div className="text-[10px] text-emerald-700 mt-1">{isEn ? "Price is above the average" : "Le prix est AU-DESSUS de la moyenne"}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Le Verdict */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm md:col-span-2">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4">
                    <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl"><Target size={24}/></div>
                    <div>
                        <h3 className="text-xl font-black text-slate-900">{isEn ? "The Tactical Verdict" : "Le Verdict Tactique"}</h3>
                        <p className="text-sm text-slate-500">{isEn ? "A summary generated by our algorithm based on the combination of RSI and SMAs." : "Un résumé généré par notre algorithme en combinant le RSI et les moyennes mobiles."}</p>
                    </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50 flex flex-col items-center text-center gap-2">
                        <span className="px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 shadow-sm">{isEn ? "Oversold" : "Survente"}</span>
                        <p className="text-xs text-emerald-800 font-medium mt-1">{isEn ? "Excellent entry point. The stock has been heavily penalized." : "Excellent point d'entrée potentiel. L'action a été lourdement pénalisée."}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-rose-200 bg-rose-50 flex flex-col items-center text-center gap-2">
                        <span className="px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider bg-rose-100 text-rose-700 border border-rose-200 shadow-sm">{isEn ? "Overbought" : "Surachat"}</span>
                        <p className="text-xs text-rose-800 font-medium mt-1">{isEn ? "Caution. The stock is overheated, a pullback is likely." : "Prudence. L'action est en surchauffe, un repli est probable."}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center text-center gap-2">
                        <span className="px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider bg-slate-200 text-slate-700 border border-slate-300 shadow-sm">{isEn ? "Neutral" : "Neutre"}</span>
                        <p className="text-xs text-slate-600 font-medium mt-1">{isEn ? "No extreme signal. Normal market behavior." : "Aucun signal extrême. Comportement de marché classique."}</p>
                    </div>
                    <div className="p-4 rounded-xl border border-orange-200 bg-orange-50 flex flex-col items-center text-center gap-2">
                        <span className="px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider bg-orange-100 text-orange-700 border border-orange-200 shadow-sm">{isEn ? "Bearish" : "Baissier"}</span>
                        <p className="text-xs text-orange-800 font-medium mt-1">{isEn ? "Price is trading below its key moving averages." : "Le prix évolue sous ses moyennes mobiles clés."}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const CQPExplainer = ({ isEn }) => (
    <div className="max-w-5xl mx-auto space-y-8">
        <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm text-center">
            <h2 className="text-3xl font-black text-slate-900 mb-4">{isEn ? "The" : "La Philosophie"} <span className="text-rose-600">CQP</span> {isEn ? "Philosophy" : ""}</h2>
            <div className="inline-block bg-rose-50 text-rose-800 font-bold px-4 py-1 rounded-full text-sm mb-4">
                {isEn ? "Growth • Quality • Reasonable Price" : "Croissance • Qualité • Prix Raisonnable"}
            </div>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                {isEn 
                ? "The CQP strategy is the core of our investment approach. It is the compass that determines which companies deserve to enter our portfolio before we even look at a stock chart." 
                : "La stratégie CQP est le cœur de notre approche d'investissement. C'est la boussole qui détermine quelles entreprises méritent d'entrer dans notre portefeuille avant même de regarder le graphique boursier."}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><TrendingUp size={100} className="text-emerald-600"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">C</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "Growth" : "Croissance"}</h3>
                    <p className="text-sm text-slate-500 mb-4">{isEn ? "The engine of future performance." : "Le moteur de la performance future."}</p>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">{isEn ? "Growth" : "Croissance"} &gt; 15% :</span> {isEn ? "Sign of a rapidly expanding company." : "Signe d'une entreprise en pleine expansion."}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-blue-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">{isEn ? "Future Estimates:" : "Estimations Futures :"}</span> {isEn ? "We favor what's coming (Analysts), not just the past." : "On privilégie ce qui va arriver (Analystes), pas uniquement le passé."}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><Shield size={100} className="text-rose-600"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">Q</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "Quality (Health)" : "Qualité (Santé)"}</h3>
                    <p className="text-sm text-slate-500 mb-4">{isEn ? "The financial technical inspection." : "Le contrôle technique financier."}</p>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-2 text-sm">
                            <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">Piotroski &lt; 4 :</span> {isEn ? "Risk of bankruptcy or degradation to avoid." : "Risque de faillite ou de dégradation à éviter."}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">Score 7-9 :</span> {isEn ? "Very healthy company, solid margins, debt under control." : "Entreprise très saine, marges solides, dettes sous contrôle."}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><Scale size={100} className="text-indigo-600"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">P</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "Reasonable Price" : "Prix Raisonnable"}</h3>
                    <p className="text-sm text-slate-500 mb-4">{isEn ? "We avoid overpaying for a gem." : "On évite de surpayer une pépite."}</p>
                    <ul className="space-y-3">
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">PE &lt; 15 :</span> {isEn ? "Discounted or cheap stock ('Value' style)." : "Action décotée ou bon marché (Style 'Value')."}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm">
                            <XCircle size={16} className="text-rose-500 mt-1 shrink-0"/>
                            <span><span className="font-bold">PE &gt; 35 :</span> {isEn ? "Expensive stock. Acceptable only if the company is in justified hyper-growth ('Growth' style)." : "Action chère. Acceptable uniquement si l'entreprise est en hyper-croissance justifiée (Style 'Growth')."}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
);

const QuantamentalExplainer = ({ isEn }) => (
    <div className="max-w-5xl mx-auto space-y-8">
        <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm text-center">
            <h2 className="text-3xl font-black text-slate-900 mb-4">{isEn ? "DCA Algorithm:" : "Algorithme DCA :"} <span className="text-indigo-600">Quantamental V4.0</span></h2>
            <div className="inline-block bg-indigo-50 text-indigo-800 font-bold px-4 py-1 rounded-full text-sm mb-4">
                {isEn ? "CQP Philosophy + Technical Timing + Diversification" : "Philosophie CQP + Timing Technique + Diversification"}
            </div>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                {isEn 
                ? "If CQP tells you WHAT to buy, the Quantamental DCA tells you WHEN and HOW MUCH to invest. It's a capital allocation robot that reviews all your stocks and mathematically distributes your budget in 4 steps." 
                : "Si le CQP vous dit QUOI acheter, le DCA Quantamental vous dit QUAND et COMBIEN investir. C'est un robot d'allocation de capital qui passe en revue toutes vos actions et distribue votre budget mathématiquement selon 4 étapes."}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><TrendingUp size={100} className="text-emerald-600"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">1</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "The CQP Base" : "La Base CQP"}</h3>
                    <ul className="space-y-3 mt-4">
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0"/>
                            <span>{isEn ? "The algorithm merges estimated growth and dividend to create a score." : "L'algorithme fusionne la croissance estimée et le dividende pour créer un score."}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-blue-500 mt-1 shrink-0"/>
                            <span>{isEn ? "It penalizes the score (up to -40%) if the PE is in a bubble, or gives a bonus if it's discounted." : "Il pénalise le score (jusqu'à -40%) si le PE (Prix) est dans la bulle, ou donne un bonus si c'est décoté."}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4 opacity-10"><Activity size={100} className="text-rose-600"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">2</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "Timing (RSI)" : "Le Timing (RSI)"}</h3>
                    <ul className="space-y-3 mt-4">
                        <li className="flex items-start gap-2 text-sm">
                            <CheckCircle size={16} className="text-emerald-500 mt-1 shrink-0"/>
                            <span><span className="font-bold text-emerald-600">RSI &lt; 30 :</span> {isEn ? "The stock has plummeted. The algo gives +2 points to buy the dip." : "L'action a violemment chuté. L'algo donne +2 points pour acheter le creux."}</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm">
                            <XCircle size={16} className="text-rose-500 mt-1 shrink-0"/>
                            <span><span className="font-bold text-rose-600">RSI &gt; 70 :</span> {isEn ? "Extreme overheating. The algo deducts -2 points to prevent buying at the top." : "Surchauffe extrême. L'algo enlève -2 points pour vous empêcher d'acheter au plus haut."}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm relative overflow-hidden md:col-span-2">
                <div className="absolute top-0 right-0 p-4 opacity-10"><Shield size={100} className="text-amber-500"/></div>
                <div className="relative z-10">
                    <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center mb-4 font-black text-xl">3</div>
                    <h3 className="text-xl font-black text-slate-900 mb-2">{isEn ? "The 'Safety Brake' (Diversification)" : "Le 'Frein de Sécurité' (Diversification)"}</h3>
                    <ul className="space-y-3 mt-4">
                        <li className="flex items-start gap-2 text-sm">
                            <AlertTriangle size={16} className="text-amber-500 mt-1 shrink-0"/>
                            <span>{isEn ? "If a stock already represents more than 15% of your total portfolio, the algo divides its score by 10 to force diversification." : "Si une action représente déjà plus de 15% de votre portefeuille total, l'algo divise son score par 10 pour vous forcer à diversifier."}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
);

const BalanceSheetVisual = ({ isEn }) => (
    <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-lg max-w-5xl mx-auto">
        <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-slate-900 mb-2">{isEn ? "The Balance Sheet" : "Le Bilan Comptable"}</h2>
            <p className="text-slate-500">{isEn ? "A snapshot of the company's wealth at a given time." : "C'est une photo du patrimoine de l'entreprise à un instant T."}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
            <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-emerald-100 text-emerald-700 rounded-xl"><TrendingUp size={24}/></div>
                    <h3 className="text-2xl font-bold text-emerald-800">{isEn ? "Assets" : "L'Actif (Assets)"}</h3>
                </div>
                <div className="bg-emerald-50 rounded-2xl p-6 border-2 border-emerald-100 space-y-4 h-full">
                    <div className="bg-white p-4 rounded-xl border border-emerald-100 shadow-sm">
                        <div className="font-bold text-emerald-800">{isEn ? "1. Current Assets" : "1. Actif Courant (Court terme)"}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <span className="badge-green">Cash</span>
                            <span className="badge-green">{isEn ? "Inventory" : "Stocks"}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-200 rounded-full p-2 z-10"><Scale className="text-slate-400" /></div>
            <div className="space-y-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-rose-100 text-rose-700 rounded-xl"><Scale size={24}/></div>
                    <h3 className="text-2xl font-bold text-rose-800">{isEn ? "Liabilities & Equity" : "Passif & Capitaux"}</h3>
                </div>
                <div className="bg-rose-50 rounded-2xl p-6 border-2 border-rose-100 space-y-4 h-full flex flex-col">
                    <div className="bg-white p-4 rounded-xl border border-rose-100 shadow-sm flex-1">
                        <div className="font-bold text-rose-800">{isEn ? "1. Liabilities" : "1. Le Passif (Liabilities)"}</div>
                        <div className="mt-2 space-y-2">
                            <div className="flex justify-between text-sm"><span className="text-slate-600">{isEn ? "Bank Loans" : "Emprunts"}</span></div>
                        </div>
                    </div>
                    <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100 shadow-sm flex-1">
                        <div className="font-bold text-indigo-800">{isEn ? "2. Equity" : "2. Capitaux Propres (Equity)"}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const MetricCard = ({ item, isEn }) => (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all h-full flex flex-col">
        <div className="flex justify-between items-start mb-4">
            <div>
                <h3 className="text-xl font-black text-slate-800">{item.acronym}</h3>
                <p className="text-xs font-bold text-slate-400 uppercase">{item.name}</p>
            </div>
            <div className={`p-2 rounded-lg bg-${item.color}-50 text-${item.color}-600`}><item.icon size={24} /></div>
        </div>
        <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow">{item.definition}</p>
        <div className="bg-slate-50 rounded-xl p-4 space-y-4">
            <div>
                <p className="text-xs font-bold text-slate-400 uppercase mb-1 flex items-center gap-1"><BookOpen size={12}/> {isEn ? "The Analogy" : "L'Analogie"}</p>
                <p className="text-sm text-slate-700 italic border-l-2 border-indigo-200 pl-3">"{item.analogy}"</p>
            </div>
        </div>
    </div>
);

const definitionsFr = {
    VALUATION: [
        { acronym: 'PER', name: 'Price Earning Ratio', icon: Calculator, color: 'indigo', definition: "Le rapport entre le prix de l'action et les bénéfices de l'entreprise. C'est le thermomètre le plus courant pour savoir si une action est chère.", analogy: "Si vous achetez une boulangerie qui gagne 10 000€ par an pour 200 000€, le PER est de 20." },
        { acronym: 'PEG', name: 'Price/Earnings to Growth', icon: TrendingUp, color: 'indigo', definition: "Le PER ajusté par la croissance. Il permet de justifier de payer cher une entreprise qui grandit très vite.", analogy: "Payer une Ferrari cher est normal (PEG proche de 1). Payer une Twingo au prix d'une Ferrari est une erreur (PEG > 2)." }
    ],
    QUALITY: [
        { acronym: 'ROIC', name: 'Return on Invested Capital', icon: Activity, color: 'emerald', definition: "Mesure l'efficacité de l'entreprise à transformer l'argent investi (dette + capitaux) en profits.", analogy: "Si vous donnez 100€ à l'entreprise pour construire une usine et qu'elle génère 20€ par an, son ROIC est de 20%. C'est la puissance du moteur." }
    ],
    HEALTH: [
        { acronym: 'Piotroski', name: 'Piotroski F-Score', icon: Shield, color: 'blue', definition: "Une note de 0 à 9 qui évalue l'amélioration de la santé financière d'une année sur l'autre.", analogy: "C'est le bulletin scolaire. 9/9 = L'élève progresse partout. 2/9 = L'élève décroche." }
    ],
    CONCEPTS: [
        { acronym: 'RSI', name: 'Relative Strength Index', icon: Activity, color: 'purple', definition: "Un thermomètre allant de 0 à 100 qui mesure la vitesse des changements de prix d'une action. Il indique si une action est temporairement 'surchargée' ou 'soldée'.", analogy: "C'est comme un élastique : s'il est trop étiré vers le haut (Surachat), il risque de claquer et de redescendre. S'il est trop tendu vers le bas (Survente), il va rebondir." },
        { acronym: 'CAGR', name: 'Compound Annual Growth Rate', icon: TrendingUp, color: 'purple', definition: "Le taux de croissance annuel moyen lissé. Il gomme la volatilité pour montrer la tendance réelle.", analogy: "Si vous montez une montagne russe mais finissez plus haut, le CAGR trace une ligne droite du départ à l'arrivée." }
    ]
};

const definitionsEn = {
    VALUATION: [
        { acronym: 'PER', name: 'Price Earning Ratio', icon: Calculator, color: 'indigo', definition: "The ratio between the stock price and the company's earnings. It's the most common thermometer to know if a stock is expensive.", analogy: "If you buy a bakery that earns €10,000 a year for €200,000, the PER is 20." },
        { acronym: 'PEG', name: 'Price/Earnings to Growth', icon: TrendingUp, color: 'indigo', definition: "The PER adjusted for growth. It justifies paying a high price for a fast-growing company.", analogy: "Paying a high price for a Ferrari is normal. Paying Ferrari money for a cheap car is a mistake." }
    ],
    QUALITY: [
        { acronym: 'ROIC', name: 'Return on Invested Capital', icon: Activity, color: 'emerald', definition: "Measures the company's efficiency in turning invested money into profits.", analogy: "If you give €100 to build a factory and it generates €20 a year, the ROIC is 20%. It's the engine's power." }
    ],
    HEALTH: [
        { acronym: 'Piotroski', name: 'Piotroski F-Score', icon: Shield, color: 'blue', definition: "A score from 0 to 9 evaluating the improvement of financial health year over year.", analogy: "It's the report card. 9/9 = progressing everywhere. 2/9 = dropping out." }
    ],
    CONCEPTS: [
        { acronym: 'RSI', name: 'Relative Strength Index', icon: Activity, color: 'purple', definition: "A thermometer from 0 to 100 measuring the speed of price changes. It indicates if a stock is temporarily 'overbought' or 'oversold'.", analogy: "Like a rubber band: if stretched too far up (Overbought), it might snap back. If stretched too far down (Oversold), it will bounce." },
        { acronym: 'CAGR', name: 'Compound Annual Growth Rate', icon: TrendingUp, color: 'purple', definition: "The smoothed average annual growth rate. It erases volatility to show the real trend.", analogy: "If you ride a roller coaster but end up higher, CAGR draws a straight line from start to finish." }
    ]
};

export default GuidePage;
